import { useState, useEffect, useRef, useCallback } from 'react';
import { FaXmark } from 'react-icons/fa6';
import { FaSpinner } from 'react-icons/fa';

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 0.25;

/**
 * In-app full-screen image viewer with pan/zoom — renders as an overlay
 * inside the application (not a new browser tab/window), so it works the
 * same whether the source image came from a base64 payload or an
 * authenticated backend fetch.
 */
export default function ImageZoomModal({ open, onClose, src, fallbackSrc, title, resolutionLabel, errorText, page = 1, pageCount = 1, onPageChange }) {
  const [scale, setScale] = useState(1);
  const [failedSrc, setFailedSrc] = useState(null);
  const [baseScale, setBaseScale] = useState(1);
  const [naturalSize, setNaturalSize] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setScale(1);
      setBaseScale(1);
      setNaturalSize(null);
    }
  }, [open, src]);

  const handleImageLoad = useCallback((e) => {
    const img = e.target;
    const vw = window.innerWidth - 80;
    const vh = window.innerHeight - 160;
    const fitW = vw / img.naturalWidth;
    const fitH = vh / img.naturalHeight;
    setBaseScale(Math.min(fitW, fitH, 1));
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
  }, []);

  const hasPages = pageCount > 1 && typeof onPageChange === 'function';

  useEffect(() => {
    if (!open) return;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((s) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, s + (e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP))));
    };
    const onKeyDown = (e) => {
      if (e.key === '+' || e.key === '=') setScale((s) => Math.min(MAX_ZOOM, s + ZOOM_STEP));
      else if (e.key === '-') setScale((s) => Math.max(MIN_ZOOM, s - ZOOM_STEP));
      else if (e.key === '0') setScale(1);
      else if (e.key === 'Escape') onClose && onClose();
      else if (e.key === 'ArrowLeft' && hasPages && page > 1) onPageChange(page - 1);
      else if (e.key === 'ArrowRight' && hasPages && page < pageCount) onPageChange(page + 1);
    };
    const node = containerRef.current;
    node?.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('keydown', onKeyDown);
    return () => {
      node?.removeEventListener('wheel', onWheel);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, hasPages, page, pageCount, onPageChange]);

  if (!open) return null;

  // If the main image fails to decode, show fallbackSrc instead of a broken icon.
  const usingFallback = !!src && failedSrc === src && !!fallbackSrc && fallbackSrc !== src;
  const displaySrc = usingFallback ? fallbackSrc : src;

  const finalScale = baseScale * scale;
  const zoomedBeyondViewport =
    naturalSize &&
    (naturalSize.width * finalScale > window.innerWidth ||
      naturalSize.height * finalScale > window.innerHeight);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex flex-col">
      <div className="px-4 py-3 bg-[#1e5045] text-white flex items-center justify-between flex-shrink-0">
        <span className="font-medium truncate">
          {title || 'Drawing'}
          {hasPages && <span className="ml-2 text-sm text-white/80">Page {page} of {pageCount}</span>}
        </span>
        <div className="flex items-center gap-3">
          {(usingFallback || resolutionLabel) && (
            <span className="text-xs text-white/70">
              {usingFallback ? 'Preview quality — full image unavailable' : resolutionLabel}
            </span>
          )}
          <button onClick={onClose} className="hover:bg-white/10 rounded p-1.5 transition-colors">
            <FaXmark size={18} />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={`flex-1 min-h-0 ${zoomedBeyondViewport ? 'overflow-auto items-start justify-start' : 'overflow-hidden items-center justify-center'} flex cursor-grab active:cursor-grabbing`}
      >
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={title || 'Drawing'}
            onLoad={handleImageLoad}
            onError={() => { if (!usingFallback) setFailedSrc(src); }}
            style={
              naturalSize
                ? { width: naturalSize.width * finalScale, height: naturalSize.height * finalScale }
                : { maxWidth: '100%', maxHeight: '100%' }
            }
            className="rounded shadow-2xl select-none"
            draggable={false}
          />
        ) : errorText ? (
          <div className="text-white/80 text-sm px-4 text-center">{errorText}</div>
        ) : (
          <div className="flex flex-col items-center gap-3 text-white/70">
            <FaSpinner className="animate-spin" size={28} />
            <span>Loading full resolution...</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 py-3 flex-shrink-0">
        {hasPages && (
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="h-10 px-4 rounded-full bg-white/90 hover:bg-white text-gray-800 text-sm font-medium shadow disabled:opacity-40 disabled:cursor-not-allowed mr-4"
            title="Previous page"
          >
            ‹ Prev
          </button>
        )}
        <button
          onClick={() => setScale((s) => Math.max(MIN_ZOOM, s - ZOOM_STEP))}
          className="w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-800 font-bold text-xl flex items-center justify-center shadow"
          title="Zoom out"
        >
          −
        </button>
        <div className="bg-black/60 text-white text-xs rounded-full px-3 py-1.5 min-w-[52px] text-center">
          {Math.round(scale * 100)}%
        </div>
        <button
          onClick={() => setScale((s) => Math.min(MAX_ZOOM, s + ZOOM_STEP))}
          className="w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-800 font-bold text-xl flex items-center justify-center shadow"
          title="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => setScale(1)}
          className="w-10 h-10 rounded-full bg-white/90 hover:bg-white text-gray-800 flex items-center justify-center shadow"
          title="Reset zoom"
        >
          ⟲
        </button>
        {hasPages && (
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            className="h-10 px-4 rounded-full bg-white/90 hover:bg-white text-gray-800 text-sm font-medium shadow disabled:opacity-40 disabled:cursor-not-allowed ml-4"
            title="Next page"
          >
            Next ›
          </button>
        )}
      </div>
    </div>
  );
}
